/**
 * §8.2 — every timestamp crosses the wire as UTC ISO-8601 and is rendered in
 * the viewer's timezone. Matchbox is US Central and client contacts may not be,
 * so the browser's own locale settings are the only correct answer here.
 */

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const DATE_ONLY = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : DATE_TIME.format(date);
}

/**
 * A timestamp, shown without its time.
 *
 * **Not for a `date` column.** `new Date('2026-03-12')` is parsed as UTC
 * midnight, so west of Greenwich this prints the day before — see
 * {@link formatDay}, which is what a calendar day wants. The distinction is
 * invisible until someone in Chicago reads a review date and disagrees with the
 * digest about which day it is.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : DATE_ONLY.format(date);
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `2026-03-12` as a local midnight, or null if it is not a calendar day.
 *
 * §8.2 makes every *timestamp* UTC on the wire, but `review_by` is a `date`:
 * a day with no instant attached, which the database, the digest, and the sweep
 * all agree is the 12th regardless of where the reader is standing. Building it
 * from the parts is what keeps the browser from turning it into the 11th.
 */
export function parseCalendarDate(iso: string): Date | null {
  if (!CALENDAR_DATE.test(iso)) return null;
  const [year, month, day] = iso.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** A `date` column, rendered as the day it names. Falls back to the raw value. */
export function formatDay(iso: string): string {
  const day = parseCalendarDate(iso);
  return day ? DATE_ONLY.format(day) : iso;
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/** "3 days ago" — what a last-seen column is actually read for (§2.4). */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const delta = then - Date.now();
  const magnitude = Math.abs(delta);
  for (const [unit, ms] of UNITS) {
    if (magnitude >= ms) return RELATIVE.format(Math.round(delta / ms), unit);
  }
  return 'just now';
}

/** Enough of a uuid to recognise, in a column too narrow for all of it. */
export function shortId(id: string): string {
  return id.slice(0, 8);
}
